import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import ChatContainer from '../../features/chat/ChatContainer';
import {
  CONSUMER_MESSAGES_PAGE_SIZE,
  useGetMessagesByConsumerIdQuery,
  useLogOpenDialogQuery,
} from '../../features/consumer/consumer.service';
import { mergeConsumerMessages } from '../../features/message/message.utils';
import { useAppSelector } from '../../hooks/defaultHooks';
import { getActualMessageChannel } from '../../selectors/selectors';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { toIsoDateTime } from '../../utils/parseServerDate';

const POLLING_INTERVAL = 15000;

type LoadedHistory = {
  consumerId?: string;
  /** `createdAt` of the oldest message the server was asked to go back from. */
  before?: string;
  windows: number;
};

const MessagePage = () => {
  const { consumerId } = useParams();
  const id = consumerId as string;
  const messageChannel = useAppSelector(getActualMessageChannel);
  // the page is not remounted when the route param changes, so the loaded
  // history is bound to the consumer it was loaded for
  const [loadedHistory, setLoadedHistory] = useState<LoadedHistory>({ windows: 0 });
  const history: LoadedHistory =
    loadedHistory.consumerId === consumerId ? loadedHistory : { windows: 0 };

  useLogOpenDialogQuery(id, {
    skip: !consumerId,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const { data: latestMessages, isLoading } = useGetMessagesByConsumerIdQuery(
    { id, size: CONSUMER_MESSAGES_PAGE_SIZE, mode: 'latest' },
    {
      skip: !consumerId,
      pollingInterval: POLLING_INTERVAL,
      refetchOnReconnect: true,
    },
  );
  const { data: historyMessages, isFetching: isFetchingHistory } = useGetMessagesByConsumerIdQuery(
    { id, size: CONSUMER_MESSAGES_PAGE_SIZE, mode: 'history', before: history.before },
    { skip: !consumerId || !history.before },
  );

  const messages = useMemo(
    // the freshest window goes last: its copy of a message wins on merge
    () => mergeConsumerMessages(historyMessages, latestMessages),
    [historyMessages, latestMessages],
  );
  const loadedWindows = history.windows + 1;
  const hasMoreMessages = messages.length >= loadedWindows * CONSUMER_MESSAGES_PAGE_SIZE;

  const loadOlderMessages = () => {
    const oldestMessage = messages[0];
    if (!oldestMessage) {
      return;
    }
    // a cursor cannot drift while new messages arrive, unlike a page offset
    setLoadedHistory({
      consumerId,
      before: toIsoDateTime(oldestMessage.createdAt) ?? undefined,
      windows: history.windows + 1,
    });
  };

  useDocumentTitle(`Messages ${messageChannel.name || messageChannel.email} | Clients`);
  return (
    <ChatContainer
      consumerId={id}
      messages={messages}
      isLoading={isLoading}
      isLoadingOlderMessages={isFetchingHistory}
      hasMoreMessages={hasMoreMessages}
      loadOlderMessages={loadOlderMessages}
    />
  );
};
export default MessagePage;
